import { useEffect } from "react"
import { useShallow } from "zustand/react/shallow"
import { useEthereumStore } from "../stores/ethereumStore"
import { useDocumentVisibility } from "../../hooks/useDocumentVisibility"

/**
 * Custom hook that provides access to Ethereum functionality.
 * This hook serves as an abstraction layer over the Zustand store,
 * handles initialization and side effects automatically.
 * Consistent with other domain hooks like useUserBalances.
 */
export const useEthereum = () => {
  const { wallet, error } = useEthereumStore(
    useShallow((state) => ({
      wallet: state.wallet,
      error: state.error,
    })),
  )
  // Extract specific values to avoid object reference issues
  const isAbstracted = wallet.isAbstracted

  const connectWallet = useEthereumStore((state) => state.connectWallet)
  const disconnectWallet = useEthereumStore((state) => state.disconnectWallet)
  const switchChain = useEthereumStore((state) => state.switchChain)
  const register = useEthereumStore((state) => state.register)
  const login = useEthereumStore((state) => state.login)
  const waitForNextBlockNumber = useEthereumStore((state) => state.waitForNextBlockNumber)

  const isVisible = useDocumentVisibility()

  // Inicialización: solo una vez globalmente (el guard está en el store)
  useEffect(() => {
    useEthereumStore.getState().initializeWallet()
  }, [])

  // Watcher: solo setup una vez cuando el primer componente monta
  useEffect(() => {
    if (isAbstracted) return

    // startWatching tiene guard interno para no duplicar
    useEthereumStore.getState().startWatching()

    // NO hacer cleanup aquí, el watcher debe persistir globalmente
  }, [isAbstracted])

  useEffect(() => {
    return useEthereumStore.getState().startBlockNumberWatching(isVisible)
  }, [isVisible])

  return {
    wallet,
    error,
    connectWallet,
    disconnectWallet,
    switchChain,
    register,
    login,
    waitForNextBlockNumber,
  }
}
